import React, { useState, useEffect, useCallback, useRef } from 'react'

import { DragDropContext } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'

import {
  Card,
  classnames,
  Button,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, number, oneOf, string, arrayOf, shape, func, oneOfType, object,
} from 'prop-types'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import ImageIcon from 'images/icons/svg/new_item_page/ImageIcon'

import EmptyState from './Components/EmptyState/EmptyState'
import LoadingCard from './Components/LoadingCard/LoadingCard'
import MediaCard from './Components/MediaCard/MediaCard'
import AddButton from '../../../Shared/AddButton/AddButton'
import AreaHeader from '../../../Shared/AreaHeader/AreaHeader'

let UseCaseAreaSidebar
let UseCaseAreaSidebarWithDnD

const MAX_USE_CASES_COUNT = 6

function UseCasesArea({
  role,
  useCases,
  currentUsername = '',
  onAdd: handleAdd,
  onRemove: handleRemove,
  onUpdate: handleUpdate,
  onLike: handleLike,
  onUnlike: handleUnlike,
  canUseLikeFeature,
  itemId = null,
  numberOfUseCases,
  isLoading,
  canDownloadSlicerFile = true,
  showUseCasesFor3dModels = false,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const pageConfig = usePageConfig()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarLoaded, setIsSidebarLoaded] = useState(false)
  const [selectedUseCase, setSelectedUseCase] = useState(null)
  const [selectedEmbedIndex, setSelectedEmbedIndex] = useState(0)
  const [shouldOpenGallery, setShouldOpenGallery] = useState(false)

  // State to track current embed index for each use case
  const [currentEmbedIndexes, setCurrentEmbedIndexes] = useState(
    useCases.reduce((acc, useCase) => {
      acc[useCase.id] = 0
      return acc
    }, {})
  )

  // Ref to access current value without causing re-renders
  const currentEmbedIndexesRef = useRef(currentEmbedIndexes)

  // Keep ref updated
  useEffect(() => {
    currentEmbedIndexesRef.current = currentEmbedIndexes
  }, [currentEmbedIndexes])

  // Update currentEmbedIndexes when useCases changes (new use cases added)
  useEffect(() => {
    setCurrentEmbedIndexes(prev => {
      const newIndexes = { ...prev }

      // Add missing use case IDs with index 0
      useCases.forEach(useCase => {
        if (!(useCase.id in newIndexes)) {
          newIndexes[useCase.id] = 0
        }
      })

      return newIndexes
    })
  }, [useCases])

  useEffect(() => {
    if (isSidebarOpen && !UseCaseAreaSidebar && !isSidebarLoaded) {
      importSidebar()
    }
  }, [isSidebarOpen, isSidebarLoaded])

  function importSidebar() {
    import('Pods/LazyLoaded/UseCaseAreaSidebar/UseCaseAreaSidebar')
      .then((module) => {
        UseCaseAreaSidebar = module.default
        // Wrap with DragDropContext for item page (NewModelUploader already has it)
        UseCaseAreaSidebarWithDnD = DragDropContext(HTML5Backend)(UseCaseAreaSidebar)
        setIsSidebarLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load UseCaseAreaSidebar:', error)
      })
  }

  function handleMouseEnter() {
    if (!isSidebarLoaded && !UseCaseAreaSidebar) {
      importSidebar()
    }
  }

  function handleTouchStart() {
    if (!isSidebarLoaded && !UseCaseAreaSidebar) {
      importSidebar()
    }
  }

  const handleOpenSidebar = () => {
    setShouldOpenGallery(false)
    setIsSidebarOpen(true)
  }

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false)
    setShouldOpenGallery(false)
    setSelectedUseCase(null)
    setSelectedEmbedIndex(0)
  }

  // Memoized - passed to multiple MediaCard instances
  const handleMediaNav = useCallback((useCaseId, directionOrIndex) => {
    setCurrentEmbedIndexes(prev => {
      const useCase = useCases.find(uc => uc.id === useCaseId)
      if (!useCase) return prev

      const maxIndex = useCase.useCaseEmbeds.length - 1
      const current = prev[useCaseId] ?? 0

      let nextIndex
      if (directionOrIndex === 'prev') {
        nextIndex = Math.max(0, current - 1)
      } else if (directionOrIndex === 'next') {
        nextIndex = Math.min(maxIndex, current + 1)
      } else {
        nextIndex = directionOrIndex
      }

      return { ...prev, [useCaseId]: nextIndex }
    })
  }, [useCases])

  // Memoized - passed to multiple MediaCard instances
  // Uses ref to avoid dependency on currentEmbedIndexes
  const handleUseCaseClick = useCallback((useCase, index) => {
    setSelectedUseCase(useCase)
    setSelectedEmbedIndex(index ?? currentEmbedIndexesRef.current[useCase.id] ?? 0)
    setShouldOpenGallery(true)
    setIsSidebarOpen(true)
  }, [])

  // Flatten embeds when there's only one use case
  const isSingleUseCase = !isLoading && useCases.length === 1
  const displayCards = isSingleUseCase && useCases[0]?.useCaseEmbeds?.length > 0
    ? useCases[0].useCaseEmbeds.map((embed, embedIndex) => ({
      useCase: useCases[0],
      embedIndex,
      id: `${useCases[0].id}-embed-${embedIndex}`,
    }))
    : useCases.map(useCase => ({
      useCase,
      embedIndex: null,
      id: useCase.id,
    }))

  const useCasesCount = isLoading ? Math.min(numberOfUseCases, MAX_USE_CASES_COUNT) : useCases.length
  const hasNoItems = useCasesCount === 0
  const shouldHideArea =
    (!is3dPrintModel && !showUseCasesFor3dModels) || (hasNoItems && !['buyer', 'seller'].includes(role))
  const shouldUseLargeImages = !isLoading && displayCards.length <= 2
  const tooltipContent = is3dPrintModel ?
    (
      <span>
        Discover real-life 3D prints created by other users using<br/>
        this 3D printable model.
      </span>
    ) :
    (
      <span>
        Browse real-world photos, videos, and renders showcasing how other users<br/>
        have used this 3D model
      </span>
    )

  if (shouldHideArea) {
    return null
  }

  return (
    <>
      <Card hasBorder isRounded customClass="use-cases-area">
        <AreaHeader
          title={is3dPrintModel
            ? '3D MAKES OF THIS MODEL'
            : 'USE CASES OF THIS MODEL'}
          tooltipContent={tooltipContent}
          tooltipId="use-cases"
          actions={
            <>
              {!isLoading && !hasNoItems && (
                <div
                  className="area-action-button"
                  onClick={handleOpenSidebar}
                  onMouseEnter={handleMouseEnter}
                  onTouchStart={handleTouchStart}
                >
                  <Button
                    icon={<Icon svgComponent={ImageIcon} />}
                    type="secondary"
                    variant="only-icon"
                    customClass="area-action-button__button"
                    size="tiny"
                  />
                  <span className="area-action-button__count">{useCasesCount}</span>
                </div>
              )}
              <AddButton
                openSidebar={handleOpenSidebar}
                onMouseEnter={handleMouseEnter}
                onTouchStart={handleTouchStart}
                ariaLabel={is3dPrintModel ? 'Add 3d make' : 'Add Use Case'}
              />
            </>
          }
        />
        <div
          className={classnames(
            'use-cases-area__body',
            hasNoItems && 'use-cases-area__body--empty',
          )}
        >
          {isLoading && Array.from({ length: useCasesCount }).map((_, index) => (
            <LoadingCard key={`loading-card-${index}`} />
          ))}
          {displayCards.map((card) => {
            const { useCase, embedIndex, id } = card
            // In flattened mode, embedIndex represents the index of the embed within the use case's embeds.
            // We use embedIndex as currentIndex to indicate which embed to display for this card.
            // Otherwise, currentIndex is determined by currentEmbedIndexes for the use case.
            const currentIndex = embedIndex !== null ? embedIndex : (currentEmbedIndexes[useCase.id] || 0)
            const isNavHidden = embedIndex !== null

            return (
              <MediaCard
                key={id}
                useCase={useCase}
                currentIndex={currentIndex}
                onUseCaseClick={handleUseCaseClick}
                onMediaNav={handleMediaNav}
                onLike={handleLike}
                onUnlike={handleUnlike}
                canUseLikeFeature={canUseLikeFeature}
                itemId={itemId}
                className="use-case-media-card--in-grid"
                isNavHidden={isNavHidden}
                useLargeImage={shouldUseLargeImages}
              />
            )
          })}
          {hasNoItems && <EmptyState />}
        </div>
      </Card>
      {isSidebarOpen && isSidebarLoaded && UseCaseAreaSidebarWithDnD && (
        <UseCaseAreaSidebarWithDnD
          productId={pageConfig?.product?.id}
          isOpen={isSidebarOpen}
          onCloseSidebar={handleCloseSidebar}
          useCases={useCases}
          currentUsername={currentUsername}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onUpdate={handleUpdate}
          initialSelectedUseCase={selectedUseCase}
          initialSelectedMediaIndex={selectedEmbedIndex}
          shouldOpenGalleryOnMount={shouldOpenGallery}
          canDownloadSlicerFile={canDownloadSlicerFile}
        />
      )}
    </>
  )
}

UseCasesArea.propTypes = {
  role: oneOf(['guest', 'user', 'buyer', 'seller']).isRequired,
  useCases: arrayOf(shape({
    id: string.isRequired,
    technology: oneOf(['fdm', 'sla', 'sls', 'poly_jet', 'metal_printing', 'metal_jet', 'other', null]),
    printerType: shape({
      id: number,
      name: string,
      createdAt: string,
      updatedAt: string,
      hidden: bool,
      manufacturerId: number,
    }),
    status: oneOf(['submitted', 'accepted', 'rejected']).isRequired,
    itemId: number.isRequired,
    username: string.isRequired,
    likeCount: number.isRequired,
    liked: bool.isRequired,
    createdAt: string.isRequired,
    updatedAt: string.isRequired,
    useCaseEmbeds: arrayOf(shape({
      id: string.isRequired,
      position: number.isRequired,
      externalId: string,
      source: oneOf(['youtube', 'vimeo', 'tiktok', 'x', 'upload', 'other']).isRequired,
      url: string,
      file: oneOfType([string, object]),
      type: oneOf(['image', 'video']).isRequired,
    })).isRequired,
  })).isRequired,
  currentUsername: string,
  onAdd: func.isRequired,
  onRemove: func.isRequired,
  onUpdate: func.isRequired,
  onLike: func.isRequired,
  onUnlike: func.isRequired,
  canUseLikeFeature: bool.isRequired,
  itemId: number,
  numberOfUseCases: number.isRequired,
  isLoading: bool.isRequired,
  canDownloadSlicerFile: bool,
  showUseCasesFor3dModels: bool,
}

export default UseCasesArea
